import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  LIVE_PLATFORMS,
  proposeLive,
  validateLiveLink,
  type LivePlatform,
} from '@/lib/lives';

interface ProposeLiveModalProps {
  open: boolean;
  onClose: () => void;
  onProposed: () => void;
}

function ProposeLiveModal({ open, onClose, onProposed }: ProposeLiveModalProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [host, setHost] = useState('');
  const [handle, setHandle] = useState('');
  const [category, setCategory] = useState('Musique');
  const [platform, setPlatform] = useState<LivePlatform>('youtube');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [products, setProducts] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const reset = () => {
    setTitle('');
    setHost('');
    setHandle('');
    setCategory('Musique');
    setPlatform('youtube');
    setDate('');
    setTime('');
    setLinkUrl('');
    setProducts('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('Vous devez être connecté pour proposer un live.');
      return;
    }
    if (!title.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    if (!host.trim()) {
      setError('Le nom de l\u2019hôte est obligatoire.');
      return;
    }
    if (!date || !time) {
      setError('Veuillez renseigner le jour et l\u2019heure du live.');
      return;
    }
    if (!validateLiveLink(platform, linkUrl)) {
      setError(
        'Le lien ne correspond pas à la plateforme choisie. Vérifiez l\u2019URL (YouTube, TikTok, Instagram, LinkedIn ou Google Drive/Meet).',
      );
      return;
    }

    const scheduledAt = new Date(`${date}T${time}:00`).toISOString();

    setLoading(true);
    try {
      await proposeLive({
        title: title.trim(),
        host: host.trim(),
        handle: handle.trim(),
        category,
        platform,
        scheduledAt,
        linkUrl: linkUrl.trim(),
        products,
      });
      setSuccess(true);
      onProposed();
      setTimeout(() => {
        reset();
        onClose();
      }, 1500);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue lors de la proposition du live.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-lg bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 shrink-0">
          <h3 className="font-heading font-semibold text-lg text-foreground-50">
            Proposer un live
          </h3>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-50 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/15 mb-3">
                <i className="ri-check-line text-2xl text-primary-400" />
              </div>
              <p className="font-heading font-semibold text-foreground-50">
                Live proposé avec succès !
              </p>
              <p className="text-sm text-foreground-400 mt-1">
                Votre live apparaîtra dans la section « Prochains lives ».
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Titre du live *
                </label>
                <input
                  type="text"
                  name="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex : Concert acoustique en direct"
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Nom de l&apos;hôte *
                  </label>
                  <input
                    type="text"
                    name="host"
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    placeholder="Ex : Kiko Golden Boy"
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Pseudo / handle
                  </label>
                  <input
                    type="text"
                    name="handle"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder="Ex : @kikogoldenboy"
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Catégorie
                </label>
                <select
                  name="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
                >
                  {['Musique', 'Showbiz', 'Actu', 'Comédie', 'Débat', 'Sport', 'Autre'].map(
                    (c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ),
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Plateforme *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {LIVE_PLATFORMS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPlatform(p.value)}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-medium transition-colors whitespace-nowrap ${
                        platform === p.value
                          ? 'border-primary-400/60 bg-primary-500/10 text-primary-400'
                          : 'border-background-300/60 text-foreground-400 hover:text-foreground-50'
                      }`}
                    >
                      <i className={p.icon} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Jour *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Heure *
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Lien du live *
                </label>
                <input
                  type="url"
                  name="link_url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
                <p className="text-xs text-foreground-400 mt-1.5">
                  Seuls les liens YouTube, TikTok, Instagram, LinkedIn et Google Drive/Meet sont
                  acceptés.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Produits à présenter
                </label>
                <textarea
                  name="products"
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  placeholder="Ex : Tissus wax, bijoux faits main, sacs artisanaux…"
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                />
                <p className="text-xs text-foreground-400 mt-1.5">
                  Liste les produits ou services que tu vendras pendant le live (séparés par des
                  virgules). Ils seront affichés sur ta carte de live.
                </p>
              </div>

              <div className="website_alt_field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
                  <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-accent-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Publication en cours…
                  </>
                ) : (
                  <>
                    <i className="ri-live-line" />
                    Proposer mon live
                  </>
                )}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default ProposeLiveModal;